import { useHistory } from "react-router-dom";

import type { ActionBarPrimary, ActionBarSecondary } from "../components/ActionBar";
import { I } from "../components/icons";
import { cycleSuggestion, keptKey, offerMinutes } from "../domain/cycleTraining";
import { useKaya, useTrainingDevice } from "../store/kayaStore";
import { usePlayerIntent } from "../store/playerIntent";
import { keepMySession, setSessionOverride } from "../store/toggles";

export interface CycleBar {
  lead?: string;
  primary?: ActionBarPrimary;
  secondary?: ActionBarSecondary;
}

/**
 * The session screen's cycle line (ADR-057): the sentence above Start, and in Change's place the
 * offer it names or "Keep my session". Start stays the primary and stays the day's session — the
 * offer never takes it away. Empty when `cycleSuggestion` has nothing to say.
 */
export function useCycleBar(ds: string, minutes: number, start: () => void): CycleBar {
  const S = useKaya((s) => s.S);
  const update = useKaya((s) => s.update);
  const showToast = useKaya((s) => s.showToast);
  const open = useKaya((s) => s.session);
  const intent = usePlayerIntent();
  const device = useTrainingDevice();
  const history = useHistory();

  const cyc = cycleSuggestion(S, ds);
  if (!cyc) return {};
  // with another session open, every Start goes back to it (ADR-027): the line alone
  if (open) return { lead: cyc.line };

  const primary: ActionBarPrimary = { label: `Start · ${minutes} min`, onClick: start, icon: I.play };
  const kept = cyc.keep ? keptKey(S, ds) : null;
  if (kept)
    return {
      lead: cyc.line,
      primary,
      secondary: {
        label: "Keep my session",
        onClick: () => {
          update((d) => keepMySession(d, ds));
          showToast("Your usual session is back for today");
        },
      },
    };
  const offer = cyc.offer;
  if (offer)
    return {
      lead: cyc.line,
      primary,
      secondary: {
        label: `${offer.label} · ${offerMinutes(S, offer, device)} min`,
        onClick: () => {
          if (offer.how === "short") intent.setShort();
          else update((d) => setSessionOverride(d, ds, offer.key));
          history.push("/workout", { from: history.location.pathname });
        },
      },
    };
  return { lead: cyc.line };
}
